import React, { ChangeEvent, Dispatch, SetStateAction, useState } from "react"
import tw, { styled, css } from "twin.macro"
import { useTranslation } from "next-i18next"
import { Button, ButtonTheme } from "../formElements/button"
import { ReactComponent as SpinnerSvg } from "../../assets/icons/spinner.svg"
import {
  useResendPhoneVerificationMutation,
  useSendVerifyPhoneOtpMutation,
  useVerifyPhoneOtpMutation,
} from "../../graphql/generated/graphql"
import { toast } from "react-toastify"
import { Banner, BannerTheme } from "../alert/banner"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { Input, InputType } from "../formElements/input"
import { JoinStep } from "../authentication/join"

export interface StyleProp {
  invalid: boolean
}
export const Digit = styled.div(({ invalid }: StyleProp) => [
  css`
    div {
      ${tw`min-w-min w-9 h-9 tablet:(w-10 h-10) rounded`}
      ${invalid ? tw`border-red` : tw`border-base-2`}
    }
    input {
      ${tw`text-center w-9 h-9 tablet:(w-10 h-10)`}
    }
  `,
])
export const StyledSpinner = styled(SpinnerSvg)(() => [tw`animate-spin h-6 w-6`])

export enum VerifiedState {
  Default = "default",
  Processing = "processing",
  Success = "success",
  Invalid = "invalid",
}
interface VerifyPhoneProps {
  phoneNumber: string
  phoneCountryCode: number
  setJoinStep?: Dispatch<SetStateAction<JoinStep>>
  setShowUpdateUserVerifyPhone?: Dispatch<SetStateAction<boolean>>
}

export const VerifyPhone = (props: VerifyPhoneProps) => {
  const { setShowUpdateUserVerifyPhone, phoneNumber, phoneCountryCode, setJoinStep } = props
  const { t } = useTranslation("verification")
  const [code, setCode] = useState(["", "", "", "", "", ""])
  const [verifiedState, setVerifiedState] = useState<string>(VerifiedState.Default)
  const [verifyPhoneOtp] = useVerifyPhoneOtpMutation()
  const [sendVerifyPhoneOtp] = useSendVerifyPhoneOtpMutation()
  const [resendProfileVerifyPhone] = useResendPhoneVerificationMutation()
  const disabled = verifiedState === VerifiedState.Processing || verifiedState === VerifiedState.Success
  const invalid = verifiedState === VerifiedState.Invalid

  const handleChange = (e: ChangeEvent<HTMLInputElement>, i: number) => {
    const { value } = e.target
    setCode(
      code.map((v, j) => {
        if (i === j) {
          v = value
        }
        return v
      }),
    )
    if (value) {
      const next: HTMLInputElement | null = document.querySelector(`#code${i + 1}-id`)
      next && next.focus()
    }
  }
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    const key = e.key
    if (key === "Backspace" || key === "Delete") {
      setCode(
        code.map((v, j) => {
          if (i === j) {
            v = ""
          }
          return v
        }),
      )
      const next: HTMLInputElement | null = document.querySelector(`#code${i - 1}-id`)
      next && next.focus()
    }
  }
  const handlePaste = () => {
    document.addEventListener("paste", (e) => {
      const data = e.clipboardData?.getData("Text").slice(0, 6).split("")
      data && setCode(data)
    })
  }
  const verifyCheckPhone = async () => {
    const phoneVerified = await verifyPhoneOtp({ variables: { code: code.join(""), phoneNumber, phoneCountryCode } })
    if (phoneVerified.data?.MagicSignup?.success) {
      setVerifiedState(VerifiedState.Success)
      const timer = setTimeout(() => {
        if (setShowUpdateUserVerifyPhone) {
          setShowUpdateUserVerifyPhone(false)
          toast(<Banner alertTheme={BannerTheme.Verify} text={t("notification", { field: "phone number" })} />, {
            toastId: "notifyUpdatedPhoneNumber",
            containerId: "Fixed",
          })
        } else {
          setJoinStep?.(JoinStep.CreateAccount)
        }
        clearTimeout(timer)
      }, 2000)
    } else {
      setVerifiedState(VerifiedState.Invalid)
      setCode(["", "", "", "", "", ""])
    }
  }
  const handleResendCode = async () => {
    setShowUpdateUserVerifyPhone
      ? await resendProfileVerifyPhone({ variables: { resend: true } })
      : await sendVerifyPhoneOtp({ variables: { phoneNumber, phoneCountryCode } })
  }

  useEffectAsync(async () => {
    if (code.join("").length === 6) {
      setVerifiedState(VerifiedState.Processing)
      await verifyCheckPhone()
    }
    handlePaste()
  }, [code])

  return (
    <div tw="mobile-lg:(w-[260px])">
      <form name="verify-phone-otp" autoComplete="off" tw="relative space-y-4 flex flex-col items-center text-center">
        <h3 tw="title-4">{t("verify_phone.title")}</h3>

        <div tw="flex flex-row flex-wrap justify-center space-x-1">
          {code.map((digit, i) => (
            <Digit key={i} invalid={invalid}>
              <Input
                name={`code${i}`}
                inputValue={digit}
                type={InputType.Number}
                autoFocus={i === 0}
                maxLength={1}
                handleChange={(e) => handleChange(e, i)}
                handleKeyDown={(e) => void handleKeyDown(e, i)}
                disabled={disabled}
              />
            </Digit>
          ))}
          <div tw="w-full pt-8">
            <p>{t("verify_phone.description")}</p>
            <p tw="font-bold">{`+${phoneCountryCode}${phoneNumber}`}</p>
          </div>
          {verifiedState == VerifiedState.Invalid && <p tw="w-full text-red text-sm my-1">{t("error_invalid_code")}</p>}
          {verifiedState == VerifiedState.Success && (
            <p tw="w-full text-sm my-1 text-green">{t("verify_phone.success")}</p>
          )}
        </div>
        <div tw="space-x-2">
          <div
            tw="relative w-full flex justify-center invisible"
            css={[verifiedState == VerifiedState.Processing && tw`visible`]}
          >
            <StyledSpinner />
          </div>
          <div tw="pb-8">
            <Button
              action={t("resend_code_cta")}
              theme={ButtonTheme.PrimaryGhost}
              isDisabled={disabled}
              onClickAsync={handleResendCode}
            />
          </div>
        </div>
      </form>
    </div>
  )
}
